// i18next setup shared by the game client and the editor: the kit's own strings live in the
// `common` namespace; each app adds its namespaces per language. English is bundled, every other
// language is fetched when chosen; switching re-renders every `useTranslation` user.
import i18next, { type i18n, type ResourceLanguage } from "i18next";
import { initReactI18next } from "react-i18next";

import common from "./en/common.json";
import { browserLocale, directionOf, nearest, SOURCE_LOCALE } from "./locales.ts";

export type Resources = ResourceLanguage | (() => Promise<ResourceLanguage>);

const json = (m: { default: unknown }) => m.default as ResourceLanguage[string];

const SHARED: Record<string, Resources> = {
  en: { common },
  de: () => import("./de/common.json").then((m) => ({ common: json(m) })),
  ja: () => import("./ja/common.json").then((m) => ({ common: json(m) })),
  ar: () => import("./ar/common.json").then((m) => ({ common: json(m) })),
};

let languages: Record<string, Resources> = {};
const loaded = new Set<string>();
let requests = 0;

function setDocument(tag: string): void {
  document.documentElement.lang = tag;
  document.documentElement.dir = directionOf(tag);
}

/** `resources[language]` = the app's namespaces (English bundled, others as loaders); starts in
 * English, then switches to `wanted` (else the browser's language) once loaded. */
export function initI18n(resources: Record<string, Resources>, defaultNamespace: string, wanted?: string): i18n {
  languages = resources;
  void i18next.use(initReactI18next).init({
    resources: { en: { ...(SHARED["en"] as ResourceLanguage), ...(resources["en"] as ResourceLanguage) } },
    lng: SOURCE_LOCALE,
    fallbackLng: SOURCE_LOCALE,
    defaultNS: defaultNamespace,
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    returnNull: false,
    // English is bundled: ready synchronously, no suspense.
    initAsync: false,
    react: { useSuspense: false },
  });
  loaded.add(SOURCE_LOCALE);
  setDocument(SOURCE_LOCALE);
  void setLanguage(wanted || browserLocale(uiLanguages()));
  return i18next;
}

/** Languages the UI ships text for. */
export function uiLanguages(): string[] {
  return Object.keys(languages);
}

async function bundle(r: Resources | undefined): Promise<ResourceLanguage> {
  if (!r) return {};
  return typeof r === "function" ? r() : r;
}

/** Switches the UI to the nearest shipped language of `tag` (English when none); resolves to
 * the language now shown. Number and date formats follow `i18next.language`. */
export async function setLanguage(tag: string): Promise<string> {
  const lng = nearest(tag, uiLanguages()) ?? SOURCE_LOCALE;
  const mine = ++requests;
  if (!loaded.has(lng)) {
    const [own, shared] = await Promise.all([bundle(languages[lng]), bundle(SHARED[lng])]);
    for (const [ns, data] of Object.entries({ ...shared, ...own })) i18next.addResourceBundle(lng, ns, data, true, true);
    loaded.add(lng);
  }
  // A later switch overtook this one while it loaded.
  if (mine !== requests) return i18next.language;
  await i18next.changeLanguage(lng);
  setDocument(lng);
  return lng;
}
